import { Link, useLocation, useParams } from 'react-router-dom';
import { useLoading } from '@/providers/LoadingProvider';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { useCurrentPaymentTask } from '@/providers/PaymentTaskProvider';
import { useOrderAllocatedExpenses } from '@/hooks/api/1192_order_allocated_expenses';
import { useEffect, useState } from 'react';
import { isBackofficeURL } from '@/utils';
import { PaymentTaskLogic } from '@/hooks/api/models/logics';
import { useRelatedOrders } from '@/hooks/api/0640_related_orders';

export const OrderAllocatedExpenses = ({ disabled }: { disabled: boolean }) => {
  const location = useLocation();
  const { setShowLoading } = useLoading();
  const { _w_, _t_, me, meTeamMember } = useCurrentTeamMember();
  const { purchase_order_id } = useParams();
  const { paymentTask: task } = useCurrentPaymentTask();
  const { data: relatedOrders } = useRelatedOrders({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: Number(purchase_order_id),
  });
  const { data: orderAllocatedExpenses } = useOrderAllocatedExpenses({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    purchase_order_id: Number(purchase_order_id),
    task_id: task!.id,
  });
  const [editPagePath, setEditPagePath] = useState<string>('');

  useEffect(() => {
    if (isBackofficeURL(location.pathname)) {
      setEditPagePath(
        (_) =>
          `/w/${_w_!.identifier}/backoffice/teams/${_t_!.id}/purchase_orders/${purchase_order_id}/payment_tasks/${
            task!.id
          }/payment_task_order_allocated_expenses`
      );
    } else {
      setEditPagePath(
        (_) =>
          `/w/${_w_!.identifier}/teams/${_t_!.id}/purchase_orders/${purchase_order_id}/payment_tasks/${
            task!.id
          }/payment_task_order_allocated_expenses`
      );
    }
  }, [location.pathname]);

  const [allocatedAmount, setAllocatedAmount] = useState<number>(0);
  const [unAllocatedAmount, setUnAllocatedAmount] = useState<number>(0);
  const [stateClass, setStateClass] = useState<string>('checklist-item-secondary');

  useEffect(() => {
    if (orderAllocatedExpenses) {
      const _allocatedAmount = PaymentTaskLogic.allocatedAmount(task!, orderAllocatedExpenses);
      const _unAllocatedAmount = PaymentTaskLogic.unAllocatedAmount(task!, orderAllocatedExpenses);
      setAllocatedAmount((_) => _allocatedAmount);
      setUnAllocatedAmount((_) => _unAllocatedAmount);
      setStateClass((_) => {
        if (_allocatedAmount == 0) {
          return 'checklist-item-secondary';
        } else if (_unAllocatedAmount == 0) {
          return 'checklist-item-success';
        } else {
          return 'checklist-item-danger';
        }
      });
    }
  }, [task, orderAllocatedExpenses]);

  // 請求金額確定までは非表示
  if (task!.payment_amount == null) return <></>;

  if (orderAllocatedExpenses == null) return <></>;

  // 関連プロジェクトが無い、かつ費用按分が無い場合は非表示
  if ((relatedOrders == null || relatedOrders.length == 0) && orderAllocatedExpenses.length == 0) return <></>;

  return (
    <li className="list-group-item border-0 flex-column align-items-start ps-0 py-0 mb-3">
      <div className={`checklist-item ps-2 ms-3 ${stateClass}`}>
        <div className="ms-1 d-flex align-items-center">
          <div className="flex-1">
            <h6 className="mb-0 text-dark font-weight-bold text-sm">プロジェクト別按分金額の投入</h6>
            <div className="text-weak text-xs">支払金額を、各プロジェクトの費用に分類してください。</div>
          </div>
          <Link
            to={editPagePath}
            className={`btn btn-outline-success btn-icon-only mb-0 mt-3 flex-shrink-0 ${disabled && 'disabled'}`}
          >
            <i className="fa fa-edit"></i>
          </Link>
        </div>
        <div className="ms-1 mt-3 ps-1">
          <div className="d-flex justify-content-end">
            <div className="table-responsive">
              <table className="table align-items-center">
                <thead>
                  <tr>
                    <th
                      className="text-secondary text-xxs font-weight-bolder opacity-7 px-2 text-end"
                      style={{ width: 150 }}
                    >
                      投入金額合計
                    </th>
                    <th
                      className="text-secondary text-xxs font-weight-bold opacity-7 px-2 text-end"
                      style={{ width: 150 }}
                    >
                      未分類金額合計
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="text-end text-dark text-xs">¥{allocatedAmount.toLocaleString()}</td>
                    <td className="text-end text-dark text-xs">¥{unAllocatedAmount.toLocaleString()}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div className="table-responsive w-100">
            <table className="table align-items-center">
              <thead>
                <tr>
                  <th className="text-secondary text-xxs font-weight-bolder opacity-7 px-2">関連プロジェクト</th>
                  <th
                    className="text-secondary text-xxs font-weight-bold opacity-7 px-2 text-end"
                    style={{ width: 150 }}
                  >
                    金額
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <div className="d-flex align-items-center">
                      <div className="icon icon-sm bg-dark d-flex border-radius-md align-items-center justify-content-center flex-shrink-0">
                        <i className="fas fa-yen-sign text-white"></i>
                      </div>
                      <div className="ms-2">
                        <div className="font-weight-bold px-0 text-sm text-dark mb-1">按分対象外費用</div>
                      </div>
                    </div>
                  </td>
                  <td className="text-end text-dark text-xs">¥{task!.non_allocable_amount.toLocaleString()}</td>
                </tr>
                {orderAllocatedExpenses.map((d) => (
                  <tr key={`oae-${d.id}`}>
                    <td>
                      <div className="d-flex align-items-center">
                        <div className="icon icon-sm bg-dark d-flex border-radius-md align-items-center justify-content-center flex-shrink-0">
                          <i className="fas fa-project-diagram text-white"></i>
                        </div>
                        <div className="ms-2">
                          <div className="font-weight-bold px-0 text-sm text-dark mb-1">
                            {d.related_order.sales_order_outline.name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="text-end text-dark text-xs">¥{d.amount.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </li>
  );
};
